import { useEffect, useRef, useState } from 'react';
import { searchUsers } from '@/features/admin/services/adminService';
import type { AdminUser } from '@/shared/types';

const DEBOUNCE_MS = 300;
const RESULT_LIMIT = 10;

// Server-side searchable user picker: types debounce into GET /admin/users?search=,
// so the dropdown never has to hold the whole user base client-side no matter how
// large it grows. `initialUser` pre-fills a specific selection (e.g. opened from
// that user's row in the Users table) while still letting the admin change it.
export function UserSearchSelect({
  initialUser,
  onSelect,
}: {
  initialUser?: AdminUser | null;
  onSelect: (user: AdminUser | null) => void;
}) {
  const [query, setQuery] = useState(initialUser ? initialUser.email : '');
  const [selected, setSelected] = useState<AdminUser | null>(initialUser ?? null);
  const [results, setResults] = useState<AdminUser[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Selection already made and the query still matches it verbatim — nothing to
    // search for (avoids re-querying immediately after picking a result).
    if (selected && query === selected.email) return;
    if (!query.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      searchUsers({ search: query, limit: RESULT_LIMIT })
        .then((page) => setResults(page.users))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const pick = (user: AdminUser) => {
    setSelected(user);
    setQuery(user.email);
    setOpen(false);
    onSelect(user);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (selected) {
            setSelected(null);
            onSelect(null);
          }
        }}
        placeholder="Search by email or name…"
        aria-label="Search for a user"
        autoComplete="off"
      />
      {open && (loading || results.length > 0 || query.trim()) ? (
        <div className="user-search-select__dropdown">
          {loading ? (
            <div className="user-search-select__hint">Searching…</div>
          ) : results.length === 0 ? (
            <div className="user-search-select__hint">No users match.</div>
          ) : (
            results.map((u) => (
              <button
                key={u.id}
                type="button"
                className="user-search-select__option"
                onClick={() => pick(u)}
              >
                <span className="user-search-select__option-name">{u.name || u.email}</span>
                {u.name ? <span className="user-search-select__option-email">{u.email}</span> : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

export default UserSearchSelect;
